import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import TrackingTopBar from 'src/components/ui/TrackingTopBar/TrackingTopBar';
import { useTranslation } from 'react-i18next';
import WarningBox, { WarningBoxColorType } from 'src/components/ui/WarningBox/WarningBox';
import KeyValueCard, { KeyValueCardProps } from 'src/components/ui/KeyValueCard/KeyValueCard';
import Icon from 'src/components/ui/Icon/Icon';
import Divider from 'src/components/ui/Divider/Divider';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import NoDataPlaceholder from 'src/components/layouts/NoDataPlaceholder/NoDataPlaceholder';
import SimpleFooter from 'src/components/layouts/SimpleFooter/SimpleFooter';
import CreateSupportTicketOverlay from 'src/components/layouts/CreateSupportTicketOverlay/CreateSupportTicketOverlay';
import DeliveryStatusModal from 'src/components/modals/DeliveryStatusModal/DeliveryStatusModal';
import { formatOrderDate } from 'src/utils/general';
import { socialIcons } from 'src/entities';

import { toast } from 'react-toastify';
import styles from './styles/Tracking.module.scss';
import {
  useCustomerTrackingDetailsQuery,
  usePublicCarrierQuery,
  usePublicReturnCreate,
  useReturnableProductsQuery,
} from '../services/customerTracking';
import ProductsSearchTable from '../components/layouts/ProductsSearchTable/ProductsSearchTable';
import ProductsSearchTableMobile from '../components/layouts/ProductsSearchTable/ProductsSearchTableMobile';
import Image from '../components/ui/Image/Image';
import { getPlaceholderImageFromMimetype } from '../entities';

function Return() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { t } = useTranslation();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [supportTicketModalOpen, setSupportTicketModalOpen] = useState(false);
  const [statusInfoModalOpen, setStatusInfoModalOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedCarrier, setSelectedCarrier] = useState<any>({
    name: 'DHL Express',
    id: 3,
    logo_url: null,
  });
  const [label, setLabel] = useState<string>();
  const [selected, setSelected] = useState([]);
  const [showDetails, setShowDetails] = useState(false);
  const handleOverlayClose = () => {
    setSupportTicketModalOpen(false);
  };

  const handleModalClose = () => {
    setStatusInfoModalOpen(false);
  };

  const keyString = localStorage.getItem('trackingKey') || '';
  const { data: returnableProducts } = useReturnableProductsQuery(keyString);
  const { data: details } = useCustomerTrackingDetailsQuery(keyString);
  const { data: carrier } = usePublicCarrierQuery(keyString);
  const mutation = usePublicReturnCreate();

  const buttons = [
    {
      title: t('tracking.write_email'),
      onClick: () => {},
      tone: ButtonToneProps.SECONDARY,
    },
    {
      title: t('tracking.create_support_ticket'),
      onClick: () => setSupportTicketModalOpen(!supportTicketModalOpen),
      tone: ButtonToneProps.SECONDARY,
    },
  ];

  const orderDetails = [
    {
      label: t('tracking.seller'),
      value: details?.seller?.name,
      type: 'text',
    },
    {
      label: t('tracking.enter_order_number'),
      value: details?.order_number,
      type: 'number',
    },
    {
      label: t('tracking.order_date'),
      value: details?.order_date ? formatOrderDate(details?.order_date) : '-',
      type: 'text',
    },
  ] as KeyValueCardProps['items'];

  const productsData = returnableProducts?.returnable_product_quantity.length
    ? returnableProducts?.returnable_product_quantity
    : [];

  const handleProductsChange = (data: any) => {
    setSelected(data);
  };

  const formRef = useRef<{ submitForm: () => void }>(null);

  const triggerSubmit = () => {
    if (formRef.current) {
      formRef.current.submitForm();
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const createReturn = async () => {
    try {
      const response = await mutation.mutateAsync({
        params: {
          path: {
            key: keyString,
          },
        },
        body: {
          return_quantities: selected || [],
        },
      });
      if (response) {
        console.log(response, 'response');
        setLabel(response);
        toast.success(t('returns.return_created'));
      }

      return response;
    } catch (error) {
      toast.error(t('errors.create_return_failed'));
    }
    return null;
  };

  const handleContinue = async () => {
    if (currentStep === 1) {
      triggerSubmit();

      if (!selected.length) {
        return;
      }
    }

    if (currentStep === 2) {
      try {
        const response = await createReturn();
        if (response) {
          setCurrentStep(prevStep => prevStep + 1);
        }
      } catch (error) {
        console.error(t('errors.create_return_failed'), error);
      }
    }
  };

  useEffect(() => {
    if (currentStep === 1 && selected.length > 0) {
      setCurrentStep(prevStep => prevStep + 1);
    }
  }, [selected]);

  const handleCancel = async () => {
    setCurrentStep(prevStep => prevStep - 1);
  };

  const downloadLabel = () => {
    if (!label) {
      return;
    }
    const fileName = `return_label_${details?.order_number}.pdf`;
    const fileContent = label || '';
    const blob = new Blob([fileContent], { type: 'application/pdf' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className={classNames(styles.root)}>
      <div className={styles.topbar}>
        <TrackingTopBar imgSrc={details?.seller?.logo_url || ''} buttons={buttons} />
      </div>
      <div className={styles.main}>
        <div className={classNames(styles.content, '!hidden md:!flex')}>
          <div className="relative w-full px-20 lg:px-80 py-32 md:py-48 flex-1 flex flex-col gap-y-24">
            <div className="flex justify-between">
              <h1 className="typo-text-16--bold text-full">{t('tracking.progress')}</h1>
              <div className="typo-text-14 text-faded flex gap-8 items-center">
                <span className="capitalize">{t(`status.${details?.state}`)}</span>
                <Button
                  tone={ButtonToneProps.GHOST}
                  icon="infoCircle"
                  onClick={details?.state ? () => setStatusInfoModalOpen(true) : () => {}}
                />
              </div>
            </div>
            {details?.estimated_delivery_date && (
              <WarningBox
                title={t('tracking.estimated_delivery_date')}
                copy={details?.estimated_delivery_date ? formatOrderDate(details?.estimated_delivery_date) : '-'}
                colorType={WarningBoxColorType.Successful}
                icon="package"
              />
            )}
            <KeyValueCard
              items={orderDetails.map(({ label, value, type, imgSrc, alt }) => ({
                label,
                value,
                type,
                imgSrc,
                alt,
              }))}
            />
            <div className="flex justify-between">
              <h2 className="typo-text-16--bold text-full">{t('tracking.delivery_address')}</h2>
            </div>
            <div className="typo-text-14 flex gap-8 items-start">
              <Icon name="pin" />
              <address className="not-italic">
                <p>{details?.recipient?.address.street}</p>
                {details?.recipient?.address.street2 && <p>{details?.recipient.address.street2}</p>}
                <p>
                  <span>{details?.recipient?.address.zip_code}</span>, <span>{details?.recipient?.address.city}</span>
                </p>
                <span>{details?.recipient?.address.country_name}</span>
                <p className="text-faded">{details?.recipient?.phone}</p>
              </address>
            </div>
          </div>
        </div>
        <div className="w-full relative">
          {currentStep === 1 && (
            <div className="flex w-full">
              <div className="px-20 py-32 md:px-80 md:py-56 w-full">
                <div className="flex flex-col gap-y-12">
                  <h1 className="typo-title-36">{t('returns.return')}</h1>
                  <p className="typo-text-16 text-faded mb-[12px] md:mb-[48px]">{t('returns.return_description')}</p>
                  <div className={classNames(styles.content, '!flex md:!hidden')}>
                    <div
                      role="none"
                      className="flex justify-between w-full pb-32"
                      onClick={() => setShowDetails(!showDetails)}>
                      <span className="typo-text-16--bold">Order details</span>
                      {showDetails ? <Icon name="accordion-arrow-up" /> : <Icon name="accordion-arrow-down" />}
                    </div>
                    {showDetails && (
                      <div className="relative w-full flex-1 flex flex-col gap-y-24 pb-32">
                        <div className="flex justify-between">
                          <h1 className="typo-text-16--bold text-full">{t('tracking.progress')}</h1>
                          <div className="typo-text-14 text-faded flex gap-8 items-center">
                            <span className="capitalize">{t(`status.${details?.state}`)}</span>
                            <Button
                              tone={ButtonToneProps.GHOST}
                              icon="infoCircle"
                              onClick={details?.state ? () => setStatusInfoModalOpen(true) : () => {}}
                            />
                          </div>
                        </div>
                        {details?.estimated_delivery_date && (
                          <WarningBox
                            title={t('tracking.estimated_delivery_date')}
                            copy={
                              details?.estimated_delivery_date ? formatOrderDate(details?.estimated_delivery_date) : '-'
                            }
                            colorType={WarningBoxColorType.Successful}
                            icon="package"
                          />
                        )}
                        <KeyValueCard
                          items={orderDetails.map(({ label, value, type, imgSrc, alt }) => ({
                            label,
                            value,
                            type,
                            imgSrc,
                            alt,
                          }))}
                        />
                      </div>
                    )}
                  </div>
                </div>
                {productsData?.length ? (
                  <div>
                    {isMobile ? (
                      <ProductsSearchTableMobile
                        ref={formRef}
                        containerRef={containerRef}
                        data={productsData}
                        onSelectionChange={event => handleProductsChange(event)}
                      />
                    ) : (
                      <div className="flex flex-col gap-[12px]">
                        <ProductsSearchTable
                          ref={formRef}
                          containerRef={containerRef}
                          data={productsData}
                          isReturnPage
                          onSelectionChange={event => handleProductsChange(event)}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex justify-center items-center w-full">
                    <NoDataPlaceholder headline={t('tracking.no_products_found')} />
                  </div>
                )}
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="flex w-full">
              <div className="px-20 py-32 md:px-80 md:py-56 w-full">
                <div className="flex flex-col gap-y-12 mb-[40px]">
                  <h1 className="typo-title-36">{t('returns.return_options')}</h1>
                  <p className="typo-text-16 text-faded">{t('returns.select_return_method')}</p>
                  <span className="typo-text-16--bold my-[36px]">{t('returns.carrier_options')}</span>
                  {carrier?.length &&
                    carrier?.map(carrier => (
                      <div className="flex flex-col" key={carrier?.id}>
                        <div className="flex w-full items-center gap-[24px]">
                          <label
                            htmlFor={`carrier-${carrier?.id}`}
                            className="relative flex items-center cursor-pointer">
                            <input
                              type="radio"
                              id={`carrier-${carrier?.id}`}
                              onChange={() => setSelectedCarrier(carrier)}
                              checked={selectedCarrier?.id === carrier?.id}
                              value={carrier?.name as string}
                            />
                            <div className="w-[24px] h-[24px] rounded-full border-[1px] border-subtle flex items-center justify-center">
                              {selectedCarrier?.id === carrier?.id && (
                                <div className="w-[18px] h-[18px] rounded-full bg-primary" />
                              )}
                            </div>
                          </label>
                          <Image
                            imageUrl={carrier?.logo_url || ''}
                            alt="carrier"
                            className="w-[40px] h-[40px] rounded-md"
                            fallbackComponent={<Icon name="package" width={20} height={20} />}
                          />
                          <div className="flex flex-col gap-4">
                            <span className="typo-text-14--bold">{carrier?.name}</span>
                          </div>
                        </div>
                        <Divider className="my-[16px]" />
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="flex w-full">
              {label ? (
                <div className="px-20 py-32 md:px-80 md:py-56 w-full">
                  <div className="flex flex-col gap-y-12 mb-[40px]">
                    <h1 className="typo-title-36">{t('returns.confirmation')}</h1>
                    <p className="typo-text-16 text-faded">{t('returns.return_created')}</p>
                    <span className="typo-text-16--bold my-[36px]">{t('returns.return_label')}</span>
                    <span className="typo-text-14">{t('returns.print_return_label')}</span>
                    <div className="flex items-center justify-between w-full rounded-xl surface-elevated-more border-subtle p-[20px]">
                      <div className="flex items-center gap-[16px]">
                        <div className="flex items-center justify-center w-[40px] h-[40px] surface-elevated border-subtle rounded-[8px]">
                          <img
                            className={classNames(styles.attachmentCard_icon, 'w-[28px] h-[34px]')}
                            src={getPlaceholderImageFromMimetype('application/pdf')}
                            alt="attachment_icon"
                          />
                        </div>
                        <span className="typo-text-14--medium">{`return_label_${details?.order_number}.pdf`}</span>
                      </div>
                      <Button
                        title={t('inbound.download')}
                        tone={ButtonToneProps.PRIMARY_BLUE}
                        className="w-full max-w-[115px] h-[32px]"
                        icon="download"
                        onClick={() => downloadLabel()}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col w-full gap-[12px] px-20 md:px-0 items-center justify-center h-full md:h-svh">
                  <span className="typo-title-36">{t('general.success')}</span>
                  <p className="typo-text-16 text-faded">{t('success.return_created')}</p>
                  <WarningBox
                    title={t('general.information')}
                    copy={
                      <>
                        {t('success.label_sent')}
                        <span className="typo-text-14--bold"> {details?.recipient?.email}</span>
                      </>
                    }
                    colorType={WarningBoxColorType.Successful}
                    className="w-full max-w-[775px] mt-[12px] md:mt-[36px]"
                  />
                </div>
              )}
            </div>
          )}

          {supportTicketModalOpen && <CreateSupportTicketOverlay onClose={handleOverlayClose} />}
          {statusInfoModalOpen && (
            <DeliveryStatusModal isOpen={statusInfoModalOpen} onClose={handleModalClose} status={details?.state} />
          )}
          <div className="flex px-[2rem] md:px-[8rem]">
            {currentStep !== 3 && (
              <div className="flex flex-col w-full">
                <Divider className="my-20" />
                <div className="flex w-full justify-end mb-20">
                  <div className="flex flex-col-reverse md:flex-row gap-[8px] max-w-none md:max-w-[165px] w-full">
                    <Button
                      title={t('general.cancel')}
                      tone={ButtonToneProps.SECONDARY}
                      onClick={() => handleCancel()}
                      disabled={currentStep === 1}
                    />
                    <Button
                      title={t('general.continue')}
                      onClick={() => handleContinue()}
                      tone={ButtonToneProps.PRIMARY_BLUE}
                      disabled={!productsData.length}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="hidden md:flex">
        <SimpleFooter socialIcons={socialIcons} />
      </div>
    </div>
  );
}

export default Return;
